// Shared vocabulary for AWS accounts (see Account).

// How Arch reaches a customer account. The gateway types are reached through
// per-service API Gateways deployed in the customer account, which is why they
// need endpoints configured; the IAM type is reached by assuming a role.
export const ACCESS_TYPES = [
  { key: 'access_via_iam', label: 'Cross Account IAM' },
  { key: 'access_via_api_gateway', label: 'API Gateway' },
  {
    key: 'access_via_api_gateway_and_iam',
    label: 'API Gateway + Cross Account IAM',
  },
];

export const API_GATEWAY_ACCESS_TYPES = ACCESS_TYPES.filter(t =>
  t.key.includes('api_gateway'),
).map(t => t.key);

const ACCESS_TYPE_LABELS = ACCESS_TYPES.reduce(
  (memo, type) => ({ ...memo, [type.key]: type.label }),
  {},
);

// Falls back to the raw value so an access type added server-side still shows
// as something rather than blank.
export const accessTypeLabel = accessType =>
  ACCESS_TYPE_LABELS[accessType] || accessType || 'Not set';

export const isApiGatewayAccessType = accessType =>
  API_GATEWAY_ACCESS_TYPES.includes(accessType);

// The endpoints attribute is an object keyed by service; this flattens it for
// display, dropping blank URLs.
export const endpointEntries = endpoints =>
  Object.entries(endpoints || {})
    .filter(([, url]) => url)
    .sort(([a], [b]) => a.localeCompare(b));

// Shape of the endpoints object, offered as a starting point when an account is
// first switched to a gateway access type.
export const PLACEHOLDER_ENDPOINTS = {
  facility: 'https://xxx.execute-api.us-west-2.amazonaws.com',
  images: 'https://yyy.execute-api.us-west-2.amazonaws.com',
  workstation: 'https://zzz.execute-api.us-west-2.amazonaws.com',
};

export const ENDPOINTS_JSON_MESSAGE =
  'Endpoints must be a JSON object, e.g. {"facility": "https://..."}';

// The forms edit endpoints as JSON text while the attribute is an object.
export const isBlankEndpointsJson = text =>
  !text || !text.trim() || text.trim() === '{}';

const asEndpointsObject = text => {
  const parsed = JSON.parse(text);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new TypeError('endpoints must be a JSON object');
  }
  return parsed;
};

export const isValidEndpointsJson = text => {
  if (isBlankEndpointsJson(text)) return true;
  try {
    asEndpointsObject(text);
    return true;
  } catch (err) {
    return false;
  }
};

// Blank clears the endpoints. Text that will not parse returns `fallback`: the
// forms validate it, so unparseable text means the field was never edited and
// the stored value should stand rather than be wiped.
export const parseEndpoints = (text, fallback = null) => {
  if (isBlankEndpointsJson(text)) return null;
  try {
    return asEndpointsObject(text);
  } catch (err) {
    return fallback;
  }
};

// The object as the forms show it: pretty-printed, or blank when unset.
export const endpointsToJson = endpoints =>
  endpoints ? JSON.stringify(endpoints, null, 2) : '';
